import { 
  collection, 
  doc, 
  onSnapshot, 
  query, 
  where, 
  orderBy,
  setDoc, 
  updateDoc, 
  addDoc,
  serverTimestamp,
  limit
} from 'firebase/firestore'
import type { 
  //Timestamp, 
  QuerySnapshot, DocumentData } from 'firebase/firestore'
import { ref, onValue, off, set, remove } from 'firebase/database'
import { db, rtdb } from '../lib/firebase'
import { store } from '../store'
import {
  setWorkItems,
  updateMultipleRuntimes,
  clearAllRuntimes,
  setLoading,
  setError,
  //upsertWorkItem
} from '../store/slices/workSlice'
import { updateDocumentStatus } from '../store/slices/sidebarSlice'
import { updateAnalysisStatus } from '../store/slices/analysisSlice'
import type { WorkItem, WorkRuntime } from '../store/slices/workSlice'

class WorkService {
  private firestoreUnsubs: Map<string, () => void> = new Map()
  private rtdbRefs: Map<string, any> = new Map()
  private currentUserId: string | null = null
  
  // Start listening to user's work items
  subscribeToUserWork(uid: string) {
    
    // Clean up previous subscriptions if user changed
    if (this.currentUserId && this.currentUserId !== uid) {
      this.unsubscribeAll()
    }
    
    this.currentUserId = uid
    store.dispatch(setLoading(true))
    
    // Helper to build query with optional orderBy for fallback support
    const buildWorkQuery = (includeOrderBy: boolean = true) => {
      const queryConstraints = [
        where('uid', '==', uid),
        ...(includeOrderBy ? [orderBy('createdAt', 'desc')] : []),
        limit(50)
      ]
      
      return query(collection(db, 'work'), ...queryConstraints)
    }
    
    // Process snapshot and update store
    const processSnapshot = (snapshot: QuerySnapshot<DocumentData>) => {
      const works: WorkItem[] = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          uid: data.uid,
          kind: data.kind,
          status: data.status,
          subject: data.subject || {},
          inputs: data.inputs || {},
          outputs: data.outputs || {},
          createdAt: data.createdAt?.toMillis?.() || Date.now(),
          updatedAt: data.updatedAt?.toMillis?.() || Date.now(),
          completedAt: data.completedAt?.toMillis?.(),
          error: data.error,
          provider: data.provider
        } as WorkItem
      })
      
      store.dispatch(setWorkItems(works))
      store.dispatch(setLoading(false))
    }
    
    // First try with orderBy (requires composite index)
    const workQuery = buildWorkQuery(true)
    
    const unsubFirestore = onSnapshot(
      workQuery, 
      processSnapshot,
      (error) => {
        console.error('WorkService: Firestore subscription error:', error)
        console.error('WorkService: Error code:', error.code)
        console.error('WorkService: Error message:', error.message)
        console.error('WorkService: Query UID:', uid)
        
        // If orderBy fails (missing index), retry without it
        if (error.code === 'failed-precondition' || error.message?.includes('index') || error.code === 'not-found') {
          console.log('WorkService: Retrying query without orderBy...')
          
          const fallbackQuery = buildWorkQuery(false)
          
          const unsubFallback = onSnapshot(
            fallbackQuery,
            (snapshot: QuerySnapshot<DocumentData>) => {
              console.log('WorkService: Fallback query successful')
              processSnapshot(snapshot)
            },
            (fallbackError) => {
              console.error('WorkService: Fallback query also failed:', fallbackError)
              console.error('WorkService: Fallback error code:', fallbackError.code)
              console.error('WorkService: Fallback error message:', fallbackError.message)
              store.dispatch(setError(fallbackError.message))
              store.dispatch(setLoading(false))
            }
          )
          
          this.firestoreUnsubs.set(uid, unsubFallback)
          return
        }
        
        store.dispatch(setError(error.message))
        store.dispatch(setLoading(false))
      }
    )
    
    this.firestoreUnsubs.set(uid, unsubFirestore)
    
    // RTDB: runtime updates
    const runtimeRef = ref(rtdb, `workRuntime/${uid}`)
    
    // Clear old runtimes first
    store.dispatch(clearAllRuntimes())
    
    onValue(
      runtimeRef, 
      (snapshot) => {
        const runtimes = snapshot.val() || {}
        
        // Convert all runtimes to proper format
        const formattedRuntimes: Record<string, WorkRuntime> = {}
        Object.entries(runtimes).forEach(([workId, runtime]: [string, any]) => {
          formattedRuntimes[workId] = {
            status: runtime.status || 'running',
            progress: runtime.progress || 0,
            message: runtime.message || '',
            fileName: runtime.fileName,
            updatedAt: runtime.updatedAt || Date.now()
          }
        })
        
        store.dispatch(updateMultipleRuntimes(formattedRuntimes))

        // Update sidebar document statuses based on runtime changes
        Object.entries(formattedRuntimes).forEach(([workId, runtime]) => {
          const workItem = store.getState().work.byId[workId]
          if (workItem?.subject?.documentId) {
            // Map runtime status to sidebar status
            const sidebarStatus = runtime.status === 'running' ? 'processing' :
                                runtime.status === 'completed' ? 'complete' :
                                runtime.status === 'failed' ? 'error' : 'pending'

            store.dispatch(updateDocumentStatus({
              documentId: workItem.subject.documentId,
              status: sidebarStatus
            }))

            // Update analysis status as well
            store.dispatch(updateAnalysisStatus({
              documentId: workItem.subject.documentId,
              status: sidebarStatus as 'pending' | 'processing' | 'complete' | 'error'
            }))
          }
        })
      },
      (error) => {
        console.error('WorkService: RTDB subscription error:', error)
      }
    )
    
    this.rtdbRefs.set(uid, runtimeRef)
  }
  
  // Create new work item
  async createWork(work: Omit<WorkItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const workRef = doc(collection(db, 'work'))
      const workData = {
        ...work,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      
      await setDoc(workRef, workData)
      
      // Add creation event
      await addDoc(collection(db, 'work', workRef.id, 'events'), {
        type: 'created',
        message: `Started ${work.kind} for ${work.subject.fileName || 'unknown'}`,
        timestamp: serverTimestamp()
      })
      
      console.log('WorkService: Created work item:', workRef.id)
      return workRef.id
    } catch (error) {
      console.error('WorkService: Error creating work:', error)
      throw error
    }
  }
  
  // Update work progress (typically called from cloud functions)
  async updateWorkProgress(
    workId: string, 
    uid: string, 
    progress: number, 
    message: string
  ) {
    try {
      // Update RTDB for real-time
      const runtimeRef = ref(rtdb, `workRuntime/${uid}/${workId}`)
      await set(runtimeRef, {
        status: 'running',
        progress,
        message,
        updatedAt: Date.now()
      })
      
      // Add progress event to Firestore
      await addDoc(collection(db, 'work', workId, 'events'), {
        type: 'progress',
        message,
        payload: { progress },
        timestamp: serverTimestamp()
      })
      
      console.log('WorkService: Updated progress for', workId, ':', progress, '%')
    } catch (error) {
      console.error('WorkService: Error updating progress:', error)
    }
  }
  
  // Complete work
  async completeWork(
    workId: string, 
    uid: string, 
    outputs: WorkItem['outputs']
  ) {
    try {
      // Update Firestore
      await updateDoc(doc(db, 'work', workId), {
        status: 'completed',
        outputs,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      
      // Clear RTDB runtime
      const runtimeRef = ref(rtdb, `workRuntime/${uid}/${workId}`)
      await remove(runtimeRef)
      
      // Add completion event
      await addDoc(collection(db, 'work', workId, 'events'), {
        type: 'completed',
        message: 'Work completed successfully',
        payload: { outputs },
        timestamp: serverTimestamp()
      })
      
      console.log('WorkService: Completed work:', workId)
    } catch (error) {
      console.error('WorkService: Error completing work:', error)
    }
  }
  
  // Mark work as failed
  async failWork(
    workId: string, 
    uid: string, 
    error: WorkItem['error']
  ) {
    try {
      // Update Firestore
      await updateDoc(doc(db, 'work', workId), {
        status: 'failed',
        error,
        updatedAt: serverTimestamp()
      })
      
      // Clear RTDB runtime
      const runtimeRef = ref(rtdb, `workRuntime/${uid}/${workId}`)
      await remove(runtimeRef)
      
      // Add failure event
      await addDoc(collection(db, 'work', workId, 'events'), {
        type: 'failed',
        message: error?.message || 'Work failed',
        payload: { error },
        timestamp: serverTimestamp()
      })
      
      console.log('WorkService: Failed work:', workId, error)
    } catch (err) {
      console.error('WorkService: Error marking work as failed:', err)
    }
  }
  
  // Cancel work
  async cancelWork(workId: string, uid: string) {
    try {
      // Update Firestore
      await updateDoc(doc(db, 'work', workId), {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      })
      
      // Clear RTDB runtime
      const runtimeRef = ref(rtdb, `workRuntime/${uid}/${workId}`)
      await remove(runtimeRef)
      
      // Add cancellation event
      await addDoc(collection(db, 'work', workId, 'events'), {
        type: 'cancelled',
        message: 'Work cancelled by user',
        timestamp: serverTimestamp()
      })
      
      console.log('WorkService: Cancelled work:', workId)
    } catch (error) {
      console.error('WorkService: Error cancelling work:', error)
    }
  }
  
  // Get work events (for debugging/audit)
  async getWorkEvents(workId: string) {
    try {
      const eventsQuery = query(
        collection(db, 'work', workId, 'events'),
        orderBy('timestamp', 'desc')
      )
      
      return new Promise((resolve) => {
        const unsub = onSnapshot(eventsQuery, (snapshot) => {
          const events = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          unsub() // One-time read
          resolve(events)
        })
      })
    } catch (error) {
      console.error('WorkService: Error getting work events:', error)
      return []
    }
  }
  
  // Clean up all subscriptions
  unsubscribeAll() {
    console.log('WorkService: Unsubscribing all listeners')
    
    // Unsubscribe Firestore listeners
    this.firestoreUnsubs.forEach(unsub => unsub())
    this.firestoreUnsubs.clear()
    
    // Unsubscribe RTDB listeners
    this.rtdbRefs.forEach(ref => off(ref))
    this.rtdbRefs.clear()
    
    // Clear runtime data from store
    store.dispatch(clearAllRuntimes())
    
    this.currentUserId = null
  }
  
  // Check if subscribed
  isSubscribed(): boolean {
    return this.currentUserId !== null
  }
  
  // Get current user ID
  getCurrentUserId(): string | null {
    return this.currentUserId
  }
}

// Export singleton instance
export const workService = new WorkService()

// Helper function to create work from file upload
export async function createExtractWork(
  uid: string,
  fileName: string,
  fileSize: number,
  fileUrl: string,
  documentId?: string
): Promise<string> {
  return workService.createWork({
    uid,
    kind: 'extract',
    status: 'pending',
    subject: {
      documentId: documentId || `doc-${Date.now()}`,
      fileName,
      fileSize
    },
    inputs: {
      fileUrl,
      schema: 'auto' // Auto-detect schema
    },
    outputs: {},
    error: undefined,
    provider: {
      name: 'reducto'
    }
  })
}
